/**
 * Problems, from projects (health.json) and from Orgus's own checks: rows, their actions (the
 * declared action the problem names, "Dispatch a fix", and a link to what it is about), and the
 * problem drawer that `?problem=<key>` opens on any page.
 */
import { ArrowUpRight, Play, Wrench } from '@orgus/ui/icons';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import type { Problem } from '@orgus/protocol';
import { Button, Drawer, ErrorState, IconButton, ListRow, RelativeTime, RowStatus, RowTitle, Skeleton } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useProjectProtocol } from './api.ts';
import { declaredAction, dispatchBlocker, fixView, mainCheckout, problemOrigin, problemSource, problemStatus, reportFor, subjectLink } from './model.ts';
import type { Target } from './model.ts';
import { openDeclaredAction, openDispatch } from './store.ts';
import { FactGrid } from '../detail/FactGrid.tsx';

/** `?problem=<key>` opens the problem drawer on whichever page the person is on. */
export function useProblemParam(): [string | null, (key: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const key = params.get('problem');
  const set = useCallback(
    (next: string | null) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next) p.set('problem', next);
          else p.delete('problem');
          return p;
        },
        { replace: !next, preventScrollReset: true },
      ),
    [setParams],
  );
  return [key, set];
}

export function WhyNext({ why, next }: { why: string; next: string }) {
  return (
    <dl className="flex flex-col gap-1 text-ui">
      <div className="flex gap-3">
        <dt className="w-12 shrink-0 text-fg-subtle">Why</dt>
        <dd className="min-w-0 text-fg-muted">{why}</dd>
      </div>
      <div className="flex gap-3">
        <dt className="w-12 shrink-0 text-fg-subtle">Next</dt>
        <dd className="min-w-0 text-fg-muted">{next}</dd>
      </div>
    </dl>
  );
}

/** What one problem offers: its declared action, a fix session, and where it lives. */
export function useProblemActions(p: Problem | undefined) {
  const { state } = useProjectProtocol();
  const { pipelines, checkouts } = useOrgus(useShallow((s) => ({ pipelines: s.pipelines, checkouts: s.checkouts })));
  return useMemo(() => {
    if (!p) return null;
    const report = p.projectId && p.machineId ? reportFor(state, p.projectId, p.machineId) : undefined;
    const decl = p.actionId ? declaredAction(report, p.actionId) : undefined;
    const main = p.projectId && p.machineId ? mainCheckout(Object.values(checkouts), p.projectId, p.machineId) : undefined;
    const link = subjectLink(p, { pipelines: Object.values(pipelines), checkouts: Object.values(checkouts) });
    return {
      decl,
      runDecl: decl && p.projectId && p.machineId ? () => openDeclaredAction({ projectId: p.projectId!, machineId: p.machineId!, actionId: decl.id }) : null,
      dispatchBlocked: dispatchBlocker(p, main),
      dispatch: () => openDispatch(p.key),
      link,
    };
  }, [p, state, pipelines, checkouts]);
}

/** Navigate to a subject: sessions open in the Sessions page's drawer. */
export function useGoTo(): (to: Target) => void {
  const navigate = useNavigate();
  return useCallback((to: Target) => navigate('session' in to ? `/sessions?session=${encodeURIComponent(to.session)}` : to.path), [navigate]);
}

/** Hover actions on a problem row: the declared action and "Dispatch a fix", as icon buttons. */
export function ProblemRowActions({ problem }: { problem: Problem }) {
  const a = useProblemActions(problem);
  if (!a) return null;
  return (
    <span className="flex items-center gap-1">
      {a.decl && a.runDecl && <IconButton label={`${a.decl.label}…`} icon={<Play />} size="xs" onClick={a.runDecl} />}
      {!a.dispatchBlocked && <IconButton label="Dispatch a fix…" icon={<Wrench />} size="xs" onClick={a.dispatch} />}
    </span>
  );
}

/** One problem in a list: status, what, then where it came from and since when. */
export function ProblemRow({ problem, showSource = true }: { problem: Problem; showSource?: boolean }) {
  const projects = useOrgus((s) => s.projects);
  const [, open] = useProblemParam();
  const { state } = useProjectProtocol();
  const source = problemSource(problem, (id) => projects[id]?.name);
  // While Orgus fixes it on its own, that is the news; the why waits in the drawer.
  const fixing = fixView(problem.key, state?.dispatches);
  const meta = fixing?.auto ? fixing.label : problem.why;
  return (
    <ListRow
      multiline
      onOpen={() => open(problem.key)}
      lead={<RowStatus {...(fixing?.auto && fixing.tone === 'working' ? fixing : problemStatus(problem.severity))} hideLabel focusable={false} />}
      actions={<ProblemRowActions problem={problem} />}
      trail={
        <span className="hidden w-16 shrink-0 justify-end text-caption text-fg-subtle sm:flex">
          <RelativeTime ts={problem.since} />
        </span>
      }
      aria-label={`${fixing?.auto && fixing.tone === 'working' ? fixing.label : problemStatus(problem.severity).label}: ${problem.what}`}
    >
      <RowTitle
        title={problem.what}
        meta={
          <span className="flex min-w-0 gap-2">
            <span className="min-w-0 truncate" data-meta>
              {meta}
            </span>
            {showSource && (
              <span className="shrink-0" data-meta>
                {source}
              </span>
            )}
          </span>
        }
      />
    </ListRow>
  );
}

function ProblemBody({ problem }: { problem: Problem }) {
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const { state } = useProjectProtocol();
  const goTo = useGoTo();
  const view = fixView(problem.key, state?.dispatches);
  const fix = view?.fix;
  return (
    <div className="flex flex-col gap-6">
      <WhyNext why={problem.why} next={problem.next} />
      <FactGrid
        label="Problem facts"
        lines={[
          [
            { label: 'Source', value: problemSource(problem, (id) => projects[id]?.name) },
            { label: 'Found by', value: problemOrigin(problem) },
          ],
          [
            { label: 'Since', value: <RelativeTime ts={problem.since} /> },
            { label: 'Machine', value: problem.machineId ? (machines[problem.machineId]?.name ?? 'Unknown machine') : 'None' },
          ],
        ]}
      />
      {fix && view && (
        <div className="flex min-w-0 items-center gap-3 rounded-card border border-border-subtle px-3 py-2">
          <RowStatus tone={view.tone} icon={view.icon} label={view.label} />
          <span className="text-caption text-fg-subtle">
            <RelativeTime ts={fix.at} />
          </span>
          {fix.sessionId && (
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => goTo({ session: fix.sessionId! })}>
              Open session
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** The problem drawer, for `?problem=<key>`. Mounted once in the shell. */
export function ProblemDrawerHost() {
  const [key, setKey] = useProblemParam();
  return key ? <ProblemDrawer problemKey={key} onClose={() => setKey(null)} /> : null;
}

function ProblemDrawer({ problemKey: key, onClose }: { problemKey: string; onClose: () => void }) {
  const { state, phase, error, retry } = useProjectProtocol();
  const problem = key ? state?.problems.find((p) => p.key === key) : undefined;
  const a = useProblemActions(problem);
  const goTo = useGoTo();
  const st = problem ? problemStatus(problem.severity) : null;
  const primaryIsAction = !!a?.runDecl;
  return (
    <Drawer
      open
      onOpenChange={(v) => !v && onClose()}
      width={480}
      bodyClassName="px-6 py-4"
      eyebrow={st ? <RowStatus {...st} /> : undefined}
      actions={
        a?.link ? (
          <Button size="sm" variant="ghost" icon={<ArrowUpRight />} onClick={() => goTo(a.link!.to)}>
            {a.link.label}
          </Button>
        ) : undefined
      }
      title={problem?.what ?? (phase === 'loading' ? 'Loading problem' : 'Problem cleared')}
      footer={
        problem && a ? (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {!a.dispatchBlocked && (
              <Button variant={primaryIsAction ? 'secondary' : 'primary'} icon={<Wrench />} onClick={a.dispatch}>
                Dispatch a fix
              </Button>
            )}
            {a.decl && a.runDecl && (
              <Button variant="primary" icon={<Play />} onClick={a.runDecl}>
                {a.decl.label}
              </Button>
            )}
          </div>
        ) : undefined
      }
    >
      {problem ? (
        <ProblemBody problem={problem} />
      ) : phase === 'loading' ? (
        <div className="flex flex-col gap-3" role="status" aria-label="Loading problem">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : phase === 'error' ? (
        <ErrorState variant="inline" error={error} onRetry={retry} />
      ) : (
        <p className="text-ui text-fg-muted">This problem is no longer open.</p>
      )}
    </Drawer>
  );
}
